import * as React from 'react';

import { cn } from '@/lib/utils';

// =============================================================================
// cgu-tabs.tsx — Onglets accessibles 0-dépendance (WAI-ARIA Tabs pattern).
// =============================================================================
// Aucune dépendance ajoutée (pas de @radix-ui/react-tabs dans le lockfile figé). Conforme
// WCAG 2.1 AA / WAI-ARIA Authoring Practices « Tabs » :
//   - role="tablist" / role="tab" / role="tabpanel" ; aria-selected ; aria-controls / id ;
//   - navigation clavier : ← → (avec wrap), Home / Fin ; activation automatique au focus ;
//   - roving tabindex (seul l'onglet actif est dans l'ordre de tabulation).
// Contrôlé : l'onglet actif vit à l'extérieur (URL search state, RULE_REF_006).

export interface CguTabDef {
  /** Identifiant stable de l'onglet (sérialisé en URL). */
  readonly id: string;
  /** Libellé visible (porte l'information — jamais une couleur seule). */
  readonly label: string;
}

export interface CguTabsProps {
  readonly tabs: readonly CguTabDef[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
  /** Libellé accessible du groupe d'onglets (aria-label du tablist). */
  readonly ariaLabel: string;
  /** Contenu du panneau actif (un seul panneau monté à la fois). */
  readonly children: React.ReactNode;
}

export function CguTabs({
  tabs,
  activeId,
  onSelect,
  ariaLabel,
  children,
}: CguTabsProps): React.JSX.Element {
  const tabRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());

  const activeIndex = Math.max(
    0,
    tabs.findIndex((t) => t.id === activeId),
  );

  const focusTab = React.useCallback((id: string): void => {
    tabRefs.current.get(id)?.focus();
  }, []);

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>): void => {
      let nextIndex: number | null = null;
      switch (event.key) {
        case 'ArrowLeft':
          nextIndex = (activeIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'ArrowRight':
          nextIndex = (activeIndex + 1) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      const next = tabs[nextIndex];
      if (next !== undefined) {
        onSelect(next.id);
        focusTab(next.id);
      }
    },
    [activeIndex, tabs, onSelect, focusTab],
  );

  return (
    <div className="space-y-6">
      <div
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        className="flex gap-1 border-b border-border"
      >
        {tabs.map((tab) => {
          const selected = tab.id === activeId;
          return (
            <button
              key={tab.id}
              ref={(node) => {
                if (node !== null) {
                  tabRefs.current.set(tab.id, node);
                } else {
                  tabRefs.current.delete(tab.id);
                }
              }}
              type="button"
              role="tab"
              id={`cgu-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`cgu-tabpanel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onSelect(tab.id)}
              onKeyDown={onKeyDown}
              className={cn(
                'rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                selected
                  ? 'border-b-2 border-primary text-foreground'
                  : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`cgu-tabpanel-${activeId}`}
        aria-labelledby={`cgu-tab-${activeId}`}
        className="focus-visible:outline-none"
      >
        {children}
      </div>
    </div>
  );
}
